export type BillingState = Readonly<{
  subscribed: boolean;
  studentToken: string;
  studentTokenFragment: string;
  planId: string | null;
  purchasedCourseIds: ReadonlyArray<string>;
  lastCheckoutSessionId: string | null;
  updatedAt: number;
}>;

const BILLING_KEY = 'mpb_billing_state';
const STUDENT_TOKEN_KEY = 'mpb_student_token';

const isBrowser = typeof window !== 'undefined';

const createStudentToken = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `student_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export const getOrCreateStudentToken = (): string => {
  if (!isBrowser) return 'anonymous_student';

  const existing = localStorage.getItem(STUDENT_TOKEN_KEY);
  if (existing) return existing;

  const token = createStudentToken();
  localStorage.setItem(STUDENT_TOKEN_KEY, token);
  return token;
};

const toBillingState = (partial?: Partial<BillingState>): BillingState => {
  const studentToken = partial?.studentToken || getOrCreateStudentToken();
  const purchasedCourseIds = Array.isArray(partial?.purchasedCourseIds)
    ? partial.purchasedCourseIds.filter(Boolean)
    : [];

  return {
    subscribed: partial?.subscribed ?? false,
    studentToken,
    studentTokenFragment: studentToken.slice(0, 12),
    planId: partial?.planId ?? null,
    purchasedCourseIds,
    lastCheckoutSessionId: partial?.lastCheckoutSessionId ?? null,
    updatedAt: partial?.updatedAt ?? Date.now(),
  };
};

export const getBillingState = (): BillingState => {
  if (!isBrowser) return toBillingState();

  const raw = localStorage.getItem(BILLING_KEY);
  if (!raw) return toBillingState();

  try {
    const parsed = JSON.parse(raw) as Partial<BillingState>;
    return toBillingState(parsed);
  } catch {
    return toBillingState();
  }
};

export const setSubscribed = (subscribed: boolean, planId?: string | null): BillingState => {
  const next = toBillingState({
    ...getBillingState(),
    subscribed,
    planId: planId ?? null,
    updatedAt: Date.now(),
  });

  if (isBrowser) {
    localStorage.setItem(BILLING_KEY, JSON.stringify(next));
  }
  return next;
};

export const markSubscribedFromCheckout = (planId?: string | null): BillingState =>
  setSubscribed(true, planId ?? null);

export const markCoursePurchased = (courseId: string, checkoutSessionId?: string | null): BillingState => {
  const current = getBillingState();
  const normalizedCourseId = decodeURIComponent(courseId).trim().toLowerCase();
  const nextCourseIds = current.purchasedCourseIds.includes(normalizedCourseId)
    ? current.purchasedCourseIds
    : [...current.purchasedCourseIds, normalizedCourseId];

  const next = toBillingState({
    ...current,
    purchasedCourseIds: nextCourseIds,
    lastCheckoutSessionId: checkoutSessionId ?? current.lastCheckoutSessionId ?? null,
    updatedAt: Date.now(),
  });

  if (isBrowser) {
    localStorage.setItem(BILLING_KEY, JSON.stringify(next));
  }
  return next;
};

export const hasCourseAccess = (courseId: string): boolean => {
  const billing = getBillingState();
  const normalizedCourseId = decodeURIComponent(courseId).trim().toLowerCase();
  return billing.purchasedCourseIds.includes(normalizedCourseId);
};

export const getLocalPurchaseStatus = (
  courseId: string
): Readonly<{ purchased: boolean; checkoutSessionId?: string }> => {
  const billing = getBillingState();
  const normalizedCourseId = decodeURIComponent(courseId).trim().toLowerCase();
  const purchased = billing.purchasedCourseIds.includes(normalizedCourseId);

  if (!purchased) {
    return { purchased: false };
  }

  return {
    purchased: true,
    checkoutSessionId: billing.lastCheckoutSessionId || undefined,
  };
};
